import { Box } from "@mui/material";
import { useFormikContext } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  getColor,
  globalScore,
} from "../../../pages/postEditor/seoAnalyzer/helper";
import { setActivePanel } from "../../../redux/slices/panelSlice";
import { dispatch } from "../../../redux/store";
import "./styles.css";

export const SidePanel = () => {
  const { t } = useTranslation();
  const [result, setResult] = useState();
  const formikProps = useFormikContext();
  const competitorResults = useSelector(
    (state) => state.competitorAnalysis.data?.data
  );
  const scrollableRef = useRef(null);
  const activePanel = useSelector((state) => state.panelSlice.activePanel);

  useEffect(() => {
    const scores = globalScore(formikProps, competitorResults);
    setResult(scores);
    formikProps.setFieldValue("seo_score", scores?.total_score);
  }, [formikProps.values, competitorResults]);

  useEffect(() => {
    if (!activePanel) {
      scrollableRef?.current?.scrollTo(0, 0);
    }
  }, [activePanel]);

  return (
    <Box
      sx={{
        height: "100vh",
        color: "black",
        overflow: "scroll",
        px: "15px",
        pt: 3,
        width: { lg: "500px", md: "350px", xs: "100%" },
        bgcolor: "whitesmoke",
        borderLeft: "3px solid #e5e7eb",
      }}
      ref={scrollableRef}
      className="relative"
    >
      <div className="pb-14">
        <p className="text-center font-semibold mb-4">
          {t("Page-analysis", "Analyse de la page")}
        </p>

        {/* Total Score */}
        {result && (
          <>
            <div className="mb-2">
              <span className="font-semibold mb-2">{result.total_score}%</span>
            </div>
            {/* <div
              className={`text-end text-transparent bg-gradient-to-r pe-8 bg-clip-text ${getColor(
                result.total_score
              )}`}
              style={{ width: `${result.total_score}%` }}
            >
              {result.total_score}%
            </div> */}
            <div className="w-full relative p-2 rounded-xl bg-slate-300 mb-5">
              <div
                className={`bg-${getColor(
                  result.total_score
                )} h-full rounded-xl absolute top-0 left-0`}
                style={{ width: `${result.total_score}%` }}
              ></div>
            </div>
          </>
        )}

        {/* Preparation */}
        <div className="mb-4">
          <p className="font-semibold mb-2 mt-5">
            {t("Preparation", "Préparation")}
          </p>
          <div
            onClick={() => dispatch(setActivePanel("outline"))}
            className="panel-button flex items-center justify-between gap-3 p-4 w-full"
          >
            <span className="flex-1">{t("Outline", "Plan")}</span>
            {result && (
              <div className="flex items-center gap-2 w-1/3">
                <span className="text-sm">{result.plan}%</span>
                <div className="flex-1 h-2 bg-slate-300 rounded-xl overflow-hidden">
                  <div
                    className={`bg-${getColor(result.plan)} h-full rounded-xl`}
                    style={{ width: `${result.plan}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Redaction */}
        <div className="mb-4">
          <p className="font-semibold mb-2">{t("Redaction", "Rédaction")}</p>
          <div className="flex flex-col gap-5">
            {/* URL Slug */}
            <div
              onClick={() => dispatch(setActivePanel("urlSlug"))}
              className="panel-button flex items-center justify-between gap-3 p-4 w-full"
            >
              <span className="flex-1">{t("URL-Slug", "Slug d'URL")}</span>
              {result && (
                <div className="flex items-center gap-2 w-1/3">
                  <span className="text-sm">{result.url}%</span>
                  <div className="flex-1 h-2 bg-slate-300 rounded-xl overflow-hidden">
                    <div
                      className={`bg-${getColor(result.url)} h-full rounded-xl`}
                      style={{ width: `${result.url}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* H1 Title */}
            <div
              onClick={() => dispatch(setActivePanel("h1Title"))}
              className="panel-button flex items-center justify-between gap-3 p-4 w-full"
            >
              <span className="flex-1">{t("H1-tag", "Titre (Balise H1)")}</span>
              {result && (
                <div className="flex items-center gap-2 w-1/3">
                  <span className="text-sm">{result.title}%</span>
                  <div className="flex-1 h-2 bg-slate-300 rounded-xl overflow-hidden">
                    <div
                      className={`bg-${getColor(
                        result.title
                      )} h-full rounded-xl`}
                      style={{ width: `${result.title}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div
              onClick={() => dispatch(setActivePanel("content"))}
              className="panel-button flex items-center justify-between gap-3 p-4 w-full"
            >
              <span className="flex-1">{t("Content", "Contenu")}</span>
              {result && (
                <div className="flex items-center gap-2 w-1/3">
                  <span className="text-sm">{result.content}%</span>
                  <div className="flex-1 h-2 bg-slate-300 rounded-xl overflow-hidden">
                    <div
                      className={`bg-${getColor(
                        result.content
                      )} h-full rounded-xl`}
                      style={{ width: `${result.content}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Metadata */}
            <div
              onClick={() => dispatch(setActivePanel("metaData"))}
              className="panel-button flex items-center justify-between gap-3 p-4 w-full"
            >
              <span className="flex-1">{t("Metadatas", "Métadonnées")}</span>
              {result && (
                <div className="flex items-center gap-2 w-1/3">
                  <span className="text-sm">{result.metaData}%</span>
                  <div className="flex-1 h-2 bg-slate-300 rounded-xl overflow-hidden">
                    <div
                      className={`bg-${getColor(
                        result.metaData
                      )} h-full rounded-xl`}
                      style={{ width: `${result.metaData}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Optimization */}
        <div>
          <p className="font-semibold mb-2">
            {t("Optimization", "Optimisation")}
          </p>
          <div className="flex flex-col gap-5">
            {/* Semantics */}
            <div
              onClick={() => dispatch(setActivePanel("semantics"))}
              className="panel-button flex items-center justify-between gap-3 p-4 w-full"
            >
              <span className="flex-1">{t("Semantics", "Sémantique")}</span>
              {result && (
                <div className="flex items-center gap-2 w-1/3">
                  <span className="text-sm">{result.semantics}%</span>
                  <div className="flex-1 h-2 bg-slate-300 rounded-xl overflow-hidden">
                    <div
                      className={`bg-${getColor(
                        result.semantics
                      )} h-full rounded-xl`}
                      style={{ width: `${result.semantics}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Linking */}
            <div
              onClick={() => dispatch(setActivePanel("mesh"))}
              className="panel-button flex items-center justify-between gap-3 p-4 w-full"
            >
              <span className="flex-1">
                {t(
                  "Interna-and-external-linking",
                  "Maillage (interne et externe)"
                )}
              </span>
              {result && (
                <div className="flex items-center gap-2 w-1/3">
                  <span className="text-sm">{result.linking}%</span>
                  <div className="flex-1 h-2 bg-slate-300 rounded-xl overflow-hidden">
                    <div
                      className={`bg-${getColor(
                        result.linking
                      )} h-full rounded-xl`}
                      style={{ width: `${result.linking}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

const DesktopSidePanel = () => {
  return (
    <Box
      sx={{
        position: "fixed",
        right: 0,
        padding: "10px",
        left: { sm: "unset", xs: 0 },
        mt: "48px",
        zIndex: 999,
      }}
    >
      <SidePanel />
    </Box>
  );
};

export default DesktopSidePanel;
